// Publicar y despublicar en la galería pública fotos que ya están en el evento (solo el dueño).
// POST /event/{id}/public-photos acepta 50 claves por petición; la web las manda sin `public/`,
// como /photos/delete, y el backend las devuelve con el prefijo.

export const PUBLIC_CHANGE_BATCH = 50;
// Publicar lo hace el pipeline en segundo plano: se confirma releyendo la galería a los 5, 15 y 30 s.
export const PUBLISH_CHECK_DELAYS = [5000, 10000, 15000];

export const withoutPublicPrefix = (key) => (key?.startsWith("public/") ? key.slice("public/".length) : key);

/**
 * Cómo deben quedar las repetidas de una selección: con `publicCopy` se publican las normales;
 * sin él se despublican las públicas.
 */
export function classifyRepeated(existingKeys, publicKeys, publicCopy) {
    const unique = [...new Set(existingKeys)];
    return publicCopy
        ? { toPublish: unique.filter(key => !publicKeys.has(key)), toUnpublish: [] }
        : { toPublish: [], toUnpublish: unique.filter(key => publicKeys.has(key)) };
}

/**
 * Trocea en lotes de 50 y junta las respuestas. Un lote que falla cuenta como `failed`
 * sin parar los demás.
 */
export async function changeInBatches(send, keys) {
    const results = [];
    let available = null;
    for (let start = 0; start < keys.length; start += PUBLIC_CHANGE_BATCH) {
        const batch = keys.slice(start, start + PUBLIC_CHANGE_BATCH);
        try {
            const { data } = await send(batch);
            for (const item of data.results || []) results.push({ photoKey: withoutPublicPrefix(item.photoKey), status: item.status });
            if (typeof data.available === "number") available = data.available;
        } catch (error) {
            for (const photoKey of batch) results.push({ photoKey, status: "failed" });
        }
    }
    return { results, available };
}

export const keysWithStatus = (results, ...statuses) =>
    results.filter(r => statuses.includes(r.status)).map(r => r.photoKey);

// Otra operación tiene la foto unos segundos o la petición no llegó a ella: basta con reintentar.
export const RETRY_LATER = ["busy", "notStarted"];

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Cuenta cuántas de las fotos aceptadas aparecen ya en la galería. Las que no han aparecido
 * al final quedan como pendientes: el pipeline puede tardar más de 30 s y reintentar abriría
 * otra publicación. Con el cupo lleno (`full`) pueden no haber cabido, pero la galería no
 * distingue una publicación en curso de una rechazada, así que no se dan por fallidas.
 * `onProgress` recibe cada lectura; `isCancelled` corta al cambiar de evento o desmontar.
 */
export async function confirmPublished(fetchPublicPhotos, keys, { delays = PUBLISH_CHECK_DELAYS, sleep = wait, onProgress, isCancelled = () => false } = {}) {
    const expected = new Set(keys);
    let listed = new Set();
    let available = null;
    for (const delay of delays) {
        await sleep(delay);
        if (isCancelled()) break;
        try {
            const { data } = await fetchPublicPhotos();
            listed = new Set((data.photos || []).map(p => withoutPublicPrefix(p.photoKey)));
            available = data.available ?? null;
        } catch { continue; }
        const seen = listed;
        const added = [...expected].filter(key => seen.has(key)).length;
        onProgress?.({ added, listed: seen });
        if (added === expected.size) break;
    }
    const missing = [...expected].filter(key => !listed.has(key));
    return { added: expected.size - missing.length, pending: missing.length, full: available === 0, listed };
}
